import React, { ReactElement, Dispatch } from 'react';
import { Button } from '@ui/components/ui/button';
import { tabs } from '@app/constants/app';
import { TabType } from '@app/utils/types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { togglerTabButtonClass } from '@app/components/core/toggler-tab-button-class';
import { runViewTransition } from '@app/components/core/viewTransition';

/**
 * Moving the reader between sprint tabs: the cross-fade, the state, and the `?tab=` the URL is shared
 * and refreshed by. Exported because the toggler is no longer the only thing that switches tabs, and a
 * caller that set the state alone would strand the URL on the tab it left — after which the toggler
 * cannot put it right, since it ignores a tab it already believes is active.
 */
export const useSprintTabSwitch = (activeTab: TabType, setActiveTab: Dispatch<TabType>): ((tab: TabType) => void) => {
  const navigate = useNavigate();

  return (tab: TabType) => {
    if (tab === activeTab) return;
    // Cross-fade the new sprint view in, the same quick beat as the other same-level switches (see the
    // `fade` rules in globals.css). No swipe: another sprint is a sideways move, not a layer deeper.
    runViewTransition('fade', () => {
      setActiveTab(tab);
      // `replace`, so the tab stays shareable/refreshable through the URL without each click stacking a
      // history entry. Pushing would leave "back" (see useBackNavigation) popping tab changes one at a
      // time instead of leaving the page.
      navigate({ search: `?tab=${tab}` }, { replace: true });
    });
  };
};

export const SprintToggler = ({
  activeTab,
  setActiveTab,
  showOverview = true,
}: {
  activeTab: TabType;
  setActiveTab: Dispatch<TabType>;
  showOverview?: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  const setTab = useSprintTabSwitch(activeTab, setActiveTab);

  return (
    <div
      className="no-scrollbar inline-flex max-w-full items-stretch overflow-x-auto rounded-md bg-card p-1"
      data-slot="button-group"
      role="group"
    >
      <Button
        className={togglerTabButtonClass(activeTab === tabs.currentSprint)}
        onClick={() => setTab(tabs.currentSprint)}
      >
        {t('tabs.currentSprint')}
      </Button>
      <Button
        className={togglerTabButtonClass(activeTab === tabs.pastSprints)}
        onClick={() => setTab(tabs.pastSprints)}
      >
        {t('tabs.pastSprints')}
      </Button>
      {showOverview && (
        <Button className={togglerTabButtonClass(activeTab === tabs.overview)} onClick={() => setTab(tabs.overview)}>
          {t('tabs.overview')}
        </Button>
      )}
    </div>
  );
};
